/*******************************************************************************
 * Factory */
/**
* @ngdoc   factory
* @module  csm.books.services
* @name    csm.books.services.factory:Watcher
* @description
* Watches for changes on a registered object. After initializing with the
* application scope, registering an object will start a $watch on that object.
* Any changes that occur will result in a `modified` flag being set. If multiple
* objects are to be watched at the same time, create a new instance of this for
* each one.
* 
* Before making any programatic changes to the watched object, the Watcher
* should be deregistered. If the watched object is going to be replaced, or
* cleared because of a user action, the `checkForChanges` function should be
* called. This will check to see if there has been a modification, and present a
* confirmation to the user before continue with the passed in function `fn`.

* If the user agrees to the confirmation (the message and title can be set at
* time of call), then the callback function `fn` will be called, and the program
* will continue. If there have been no modifications to the object, this will
* also result in the callback function `fn` being called. Otherwise, if the user
* does not agree to the confirmation, the function is not called, and the
* execution will end; awaiting further user input.
*******************************************************************************/
WatcherFactory.$inject = ['$timeout', 'Alert'];
/* @ngInject */ export function WatcherFactory($timeout, Alert) {
  return function ($scope) {
    var mWatch = {
      modified: false,
      paused: false,

      /*************************************************************************
       * checkForChanges */
      /**
       * Checks the modified flag and displays a confirmation if true. If the
       * confirmation resolves, or there was no modification, the `fn` is called.
       *
       * @param  fn       Function to call if allowed to continue with execution.
       *                  Will be called with an empty parameter list.
       * @param  message  Custom confirmation message. Uses default if null or
       *                  empty.
       * @param  title    Custom confirmation title. Uses default if null or
       *                  empty.
       *************************************************************************/
      checkForChanges: function (fn, message, title) {
        if (mWatch.modified)
          Alert.confirm(
            message ||
              'You have unsaved changes, are you sure you want to continue?',
            title || 'Unsaved Changes'
          ).then(fn);
        else fn();
      },

      /*************************************************************************
       * deregister */
      /**
       * Clears the modified flag, then runs the `deReg` function before removing
       * it. The `deReg` function is set by the `register` function.
       *************************************************************************/
      deregister: function () {
        mWatch.setModified(false);

        /*---------------------------------------------------------------*/
        /* Run the deregistration function if it exists, then remove it. */
        /*---------------------------------------------------------------*/
        if (mWatch.deReg) {
          mWatch.deReg();
          delete mWatch.deReg;
        }
      },

      /*************************************************************************
       * pause */
      /**
       * Pauses the watcher.
       *************************************************************************/
      pause: function () {
        mWatch.paused = true;
      },

      /*************************************************************************
       * register */
      /**
       * Registers the object to be watched. $watch return a function that can be
       * called to remove the watch, so we set this to the `deReg` function for
       * later use.
       *
       * Creation of the watch expression will result in running its function
       * once, so we must clear the modified flag in a $timeout to ensure we are
       * not getting a false positive.
       *
       * @param  wo  Watched Object.
       *************************************************************************/
      register: function (wo) {
        /*------------------------------------------------------------------*/
        /* Make sure to deregister anything that was previously registered. */
        /*------------------------------------------------------------------*/
        mWatch.deregister();

        /*---------------------------------------------------------*/
        /* Started the watch expression. If there is a change,     */
        /* updated the modified flag if the watcher is not paused. */
        /*---------------------------------------------------------*/
        mWatch.deReg = $scope.$watch(
          function () {
            return wo;
          },
          function () {
            if (!mWatch.paused) mWatch.setModified(true);
          },
          true
        );

        /*---------------------------------------------------------*/
        /* Clear the modified property after the next digest       */
        /* cycle as it was set during the initialization of watch. */
        /*---------------------------------------------------------*/
        $timeout(this.setModified);
      },

      /*************************************************************************
       * setModified */
      /**
       * Creates the update function. This will set the modified flag on the
       * object being watched
       *
       * @param  modified  Modified flag to set as strict boolean.
       *************************************************************************/
      setModified: function (modified) {
        mWatch.modified = !!modified;
      },

      /*************************************************************************
       * unpause */
      /**
       * Unpauses the watcher.
       *************************************************************************/
      unpause: function () {
        mWatch.paused = false;
      }
    };

    return mWatch;
  };
}
